"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ClientErrorReport = exports.CLIENT_ERROR_LIMITS = exports.ClientErrorKind = void 0;
exports.scrubClientErrorText = scrubClientErrorText;
/**
 * SOLID Review
 * S — Strong. One concern: the wire shape of a client error report, and the one scrub both ends apply
 *     to its free text.
 * O — Strong. A new kind is one enum member; a new scrub rule is one row in SCRUB_RULES.
 * L — N/A.
 * I — Strong. One body schema, one function.
 * D — Strong. zod only.
 */
/**
 * DDD Layer: none — shared wire contract.
 *
 * `POST /api/client-errors` (client error reporting, plan 2026-10-09; handbook §12.8's "client-error
 * beacon"). The client half is frontend/src/infrastructure/diagnostics/; the server half is
 * backend/src/framework/http/routes/clientErrorsRoute.ts.
 *
 * WHAT A REPORT MAY CARRY, and what it never does. No player id, display name, e-mail, table code, JWT,
 * cookie or query string: `pagePath` is a path only (no `?` or `#`), and the free text is scrubbed.
 * Every field is bounded, and the object is `.strict()`, so a client cannot widen what is stored.
 *
 * SENT AS text/plain. `navigator.sendBeacon` with a JSON content type needs a CORS preflight; text/plain
 * does not. The body is still this schema's JSON, and the route parses it with this schema.
 */
const zod_1 = require("zod");
/**
 * Where the error was caught. `THIRD_PARTY` is a cross-origin "Script error." (an ad, the consent
 * platform): the browser hides its detail, so it is counted, never described.
 */
exports.ClientErrorKind = zod_1.z.enum(['UNCAUGHT', 'REJECTION', 'RENDER', 'PORT', 'BOOT', 'THIRD_PARTY']);
/** The longest message and stack a report carries, after scrubbing. */
exports.CLIENT_ERROR_LIMITS = Object.freeze({ message: 300, stack: 2048, pagePath: 200, count: 1000 });
const PortName = zod_1.z.string().min(1).max(60).regex(/^[A-Za-z]+$/);
exports.ClientErrorReport = zod_1.z.object({
    kind: exports.ClientErrorKind,
    message: zod_1.z.string().max(exports.CLIENT_ERROR_LIMITS.message),
    /** Same-origin frames only, scrubbed; null when there is none (a THIRD_PARTY report always). */
    stack: zod_1.z.string().max(exports.CLIENT_ERROR_LIMITS.stack).nullable(),
    /** The path the page was on — never its query or fragment, which can carry a table code. */
    pagePath: zod_1.z.string().min(1).max(exports.CLIENT_ERROR_LIMITS.pagePath).regex(/^\/[^?#\s]*$/),
    /** The entry bundle's hash, so a report names the build that raised it. */
    appVersion: zod_1.z.string().min(1).max(40).regex(/^[A-Za-z0-9._-]+$/),
    /** For a PORT report: which port, which op, which code. Null for every other kind. */
    port: PortName.nullable(),
    op: PortName.nullable(),
    code: zod_1.z.string().min(1).max(40).regex(/^[A-Z_]+$/).nullable(),
    /** How many times this page load saw it before sending — repeats are counted, not resent. */
    count: zod_1.z.number().int().min(1).max(exports.CLIENT_ERROR_LIMITS.count),
}).strict();
/**
 * THE SCRUB, DEFINED ONCE (handbook §28.51: a transformation two sides apply is shared, never copied).
 * The client applies it before sending, and the server again on receipt — the server's is the one that
 * counts, because a client can send anything.
 *
 * Order matters: a URL's query goes before the digit rule would chew it, and an e-mail before the
 * token rule could match its local part.
 */
const SCRUB_RULES = Object.freeze([
    // A URL's query string or fragment (`/?join=ABCD`, `/x#code=…`) — only after a path, so a bare `#418` in
    // "Minified React error #418" survives: that number is what the report is for.
    [/(\/[^\s?#"'`]*)([?#])[^\s"'`)]*/g, '$1$2…'],
    // An e-mail address.
    [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '<email>'],
    // A JWT: three base64url segments, the first starting `eyJ`.
    [/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g, '<jwt>'],
    // A UUID (player and room ids), a bearer token, any long opaque token.
    [/[A-Za-z0-9_-]{32,}/g, '<token>'],
    // A table code (RoomCode: four, or six, of A–Z and 2–9) named as one. Not any short capital word —
    // that would scrub JSON, HTTP and NULL — only one that follows "room", "table", "code" or "join".
    // Case-sensitive on purpose: with `i`, "table data" would lose its "data".
    [/\b([Rr]oom|[Tt]able|[Cc]ode|[Jj]oin)([\s:=#"'`]+)[A-Z2-9]{4,6}\b/g, '$1$2<code>'],
    // Any run of four or more digits: an id, a timestamp, a card-like number — but not a stack frame's
    // `:line:column`, which is what locates the bug in a minified bundle. No lookbehind: Safari before 16.4
    // cannot parse one, and a regex literal it cannot parse fails the whole module on the phones this is for.
    [/(^|[^:\d])\d{4,}/g, '$1<n>'],
]);
/** The text with every rule applied, then cut to `max` characters. */
function scrubClientErrorText(text, max) {
    let out = text;
    for (const [pattern, replacement] of SCRUB_RULES)
        out = out.replace(pattern, replacement);
    return out.length > max ? out.slice(0, max) : out;
}
//# sourceMappingURL=clientErrors.js.map